import firebaseConfigService from './firebaseConfig';
import { collection, query, where, getDocs, Timestamp, orderBy, limit, startAfter } from 'firebase/firestore';
import { db, getSyncMetadata, updateSyncMetadata, upsertIfNewer } from '../db/entityDb';

/**
 * EntitySyncService
 * Handles delta-synchronization between Firestore and Local IndexedDB.
 */
class EntitySyncService {
  constructor() {
    this.collections = ['patients', 'providers', 'lawyers', 'employers', 'insurances'];
    this.isSyncing = false;
    this.fullSyncThreshold = 1000 * 60 * 60 * 24; // 24 hours
    this.progressListeners = new Set();
    this.progress = {
      isSyncing: false,
      totalCollections: this.collections.length,
      completedCollections: 0,
      totalChanges: 0,
      currentCollection: null
    };
  }

  subscribe(listener) {
    this.progressListeners.add(listener);
    listener(this.progress);
    return () => this.progressListeners.delete(listener);
  }

  notify() {
    this.progressListeners.forEach(l => l({ ...this.progress }));
  }

  updateProgress(updates) {
    this.progress = { ...this.progress, ...updates };
    this.notify();
  }

  /**
   * Sync all entity collections in parallel
   */
  async syncAll() {
    if (this.isSyncing) return;
    this.isSyncing = true;
    this.updateProgress({ 
      isSyncing: true, 
      completedCollections: 0, 
      totalChanges: 0,
      currentCollection: null 
    });
    
    try {
      const { firestore } = firebaseConfigService.getServices();
      
      const results = [];
      for (const col of this.collections) {
        try {
          this.updateProgress({ currentCollection: col });
          const res = await this.syncCollection(firestore, col);
          results.push(res);
          this.updateProgress({ 
            completedCollections: this.progress.completedCollections + 1,
            totalChanges: this.progress.totalChanges + res.changes
          });
        } catch (colError) {
          console.error(`❌ Sync failed for collection ${col}:`, colError);
          // Don't throw, just move to next collection
          results.push({ collection: col, changes: 0, error: colError.message });
          this.updateProgress({ completedCollections: this.progress.completedCollections + 1 });
        }
      }
      
      if (this.progress.totalChanges > 0) {
        console.log(`✅ Sync Complete: ${this.progress.totalChanges} updates applied.`);
      } else {
        console.log('✅ Sync Complete: Everything up to date.');
      }
      return results;
    } catch (error) {
      console.error('❌ Entity Sync failed:', error);
      throw error;
    } finally {
      this.isSyncing = false;
      this.updateProgress({ isSyncing: false, currentCollection: null });
    }
  }

  /**
   * Sync a specific collection's deltas
   */
  async syncCollection(firestore, collectionName) {
    const lastSyncedAt = await getSyncMetadata(collectionName);
    
    // Safety: overlap by 1 second
    const sinceMs = Math.max(0, lastSyncedAt - 1000);
    const since = Timestamp.fromMillis(sinceMs);

    const colRef = collection(firestore, collectionName);
    const BATCH_SIZE = 500;
    
    let lastDoc = null;
    let totalChanges = 0;
    let newestTimestamp = lastSyncedAt;
    let hasMore = true;

    while (hasMore) {
      let q = query(
        colRef,
        where('updatedAt', '>', since),
        orderBy('updatedAt', 'asc'),
        limit(BATCH_SIZE)
      );

      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }

      const snapshot = await getDocs(q);
      if (snapshot.empty) {
        hasMore = false;
        break;
      }

      const docs = snapshot.docs.map(doc => {
        const data = doc.data();
        const updatedAtMs = data.updatedAt?.toMillis() || Date.now();
        return {
          ...data,
          id: doc.id,
          updatedAt: updatedAtMs
        };
      });

      // Safe update into IndexedDB
      await upsertIfNewer(collectionName, docs);
      
      totalChanges += docs.length;
      newestTimestamp = Math.max(newestTimestamp, ...docs.map(d => d.updatedAt));
      lastDoc = snapshot.docs[snapshot.docs.length - 1];
      
      // If we got fewer than BATCH_SIZE, we are done
      if (snapshot.docs.length < BATCH_SIZE) {
        hasMore = false;
      }

      // Update progress within collection
      console.log(`⏳ [${collectionName}] Pulled batch of ${docs.length} (Total: ${totalChanges})`);
    }

    if (totalChanges > 0) {
      await updateSyncMetadata(collectionName, newestTimestamp);
    }

    return { collection: collectionName, changes: totalChanges };
  }

  /**
   * Mirror local changes (Create/Update) immediately to cache
   */
  async upsertLocalEntity(collectionName, entity) {
    if (!this.collections.includes(collectionName)) return;
    
    // Ensure it has a local timestamp comparable to Firestore
    const data = {
      ...entity,
      updatedAt: entity.updatedAt || Date.now()
    };
    
    await db[collectionName].put(data);
    console.log(`💾 Mirrored ${collectionName} update locally: ${entity.id}`);
  }

  /**
   * Mirror local deletions immediately to cache
   */
  async deleteLocalEntity(collectionName, id) {
    if (!this.collections.includes(collectionName)) return;
    await db[collectionName].delete(id);
    console.log(`🗑️ Mirrored ${collectionName} deletion locally: ${id}`);
  }

  /**
   * Get all entities from local storage (Fast)
   */
  async getLocalEntities(collectionName) {
    return await db[collectionName].toArray();
  }

  /**
   * Clear local cache
   */
  async clearCache() {
    await Promise.all(this.collections.map(col => db[col].clear()));
    await db.syncMetadata.clear();
    console.log('🧹 Local entity cache cleared.');
  }
}

export const entitySyncService = new EntitySyncService();
export default entitySyncService;
